import {
  faBolt,
  faCircleInfo,
  faPlus,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Button,
  Grid,
  Icon,
  Input,
  Message,
  Modal,
  RadioCard,
  Table,
} from "@intility/bifrost-react";
import Drawer from "@intility/bifrost-react/Drawer";
import { useState } from "react";
import { Link } from "react-router";
import {
  useAddAudienceGrant,
  useCancelRequest,
  useClientAccess,
  useClientAudienceGrants,
  useClientRequests,
  useGrantAccess,
  useRemoveAudienceGrant,
  useRevokeAccess,
} from "../../../api/access";
import { useClients } from "../../../api/clients";
import type { RuntimeRole } from "../../../api/types";
import { RoleBadge } from "../../../components/RoleBadge";
import { formatDate } from "../../../lib/clientTypes";

type PermissionCategory = "api-access" | "data-access";
type AppGrantType = "group" | "direct";
export type Notice = { state: "success" | "warning"; text: string };

const RUNTIME_ROLES: RuntimeRole[] = ["admin", "writer", "drafter", "reader"];

// Status shown as coloured text (no pill) in the Status column.
const STATUS_META: Record<string, { label: string; color: string }> = {
  approved: { label: "Approved", color: "var(--bfc-success)" },
  pending: { label: "Pending", color: "var(--bfc-warning)" },
  denied: { label: "Denied", color: "var(--bfc-alert)" },
  cancelled: { label: "Cancelled", color: "var(--bfc-base-c-2)" },
};

// A row in the API-access table: an API this client's token may call.
interface ApiRow {
  key: string;
  api: string;
  status: string;
  date: string | null;
  onRevoke?: () => void;
  onCancel?: () => void;
}

// A row in the data-access table: an object/group this client can act on.
interface DataRow {
  key: string;
  target: string;
  role?: string;
  status: string;
  date: string | null;
  onRevoke?: () => void;
  onCancel?: () => void;
}

export function ClientAccessSection({
  clientId,
  fgaClientId,
  canEdit,
  isSpa = false,
  siblingApiClientIds = [],
}: {
  clientId: string;
  /** The readable clientId — the FGA subject is client:<clientId> (ADR-0040). */
  fgaClientId: string;
  canEdit: boolean;
  /** Whether the current client is a SPA — gates the eager-audience warning. */
  isSpa?: boolean;
  /** clientIds of API clients in the same platform — drives the quick API-access grant. */
  siblingApiClientIds?: string[];
}) {
  const { data: grants = [] } = useClientAccess(fgaClientId);
  const { data: audienceGrants = [] } = useClientAudienceGrants(clientId);
  const { data: requests = [] } = useClientRequests(clientId);
  const audienceGrant = useAddAudienceGrant(clientId);
  const removeAudience = useRemoveAudienceGrant(clientId);
  const revoke = useRevokeAccess(clientId);
  const cancel = useCancelRequest(clientId);

  // null = drawer closed; otherwise the kind of permission being added.
  const [drawerMode, setDrawerMode] = useState<PermissionCategory | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Removing a grant is confirmed in a modal first (destructive, immediate).
  const [pendingRemoval, setPendingRemoval] = useState<{
    label: string;
    onConfirm: () => void;
  } | null>(null);

  // Sibling APIs this client can call, minus the ones already
  // granted or already awaiting approval — so the quick grant only offers what's
  // actually missing, and disappears once every API is covered.
  const grantedAudiences = new Set(audienceGrants.map((g) => g.audience));
  const pendingAudiences = new Set(
    requests
      .filter((r) => r.requestType === "api-access" && r.status === "pending")
      .map((r) => r.target),
  );
  const missingApiAudiences = siblingApiClientIds.filter(
    (id) => !grantedAudiences.has(id) && !pendingAudiences.has(id),
  );

  async function handleQuickGrant() {
    setNotice(null);
    let granted = 0;
    let queued = 0;
    let failed = 0;
    for (const aud of missingApiAudiences) {
      try {
        const result = await audienceGrant.mutateAsync({ audience: aud });
        if (result.requiresApproval) queued++;
        else granted++;
      } catch {
        failed++;
      }
    }
    const parts: string[] = [];
    if (granted) parts.push(`${granted} granted`);
    if (queued) parts.push(`${queued} queued for approval`);
    if (failed) parts.push(`${failed} failed`);
    setNotice({
      state: failed ? "warning" : "success",
      text: `API access: ${parts.join(", ")}.`,
    });
  }

  // API access: approved audience grants + open api-access requests.
  const apiRows: ApiRow[] = [
    ...audienceGrants.map((g) => ({
      key: `aud:${g.audience}`,
      api: g.audience,
      status: "approved",
      date: null,
      onRevoke: () =>
        confirmRemove(g.audience, () => removeAudience.mutate(g.audience)),
    })),
    ...requests
      .filter((r) => r.requestType === "api-access" && r.status !== "approved")
      .map((r) => ({
        key: `req:${r.id}`,
        api: r.target,
        status: r.status,
        date: r.requestedAt,
        onCancel:
          r.status === "pending" ? () => cancel.mutate(r.id) : undefined,
      })),
  ];

  // Data access: approved runtime grants + open data-access requests. Group
  // targets are shown without the "group:" prefix (role reads "member").
  const dataRows: DataRow[] = [
    ...grants.map((g) => {
      const isGroup = g.object.startsWith("group:");
      return {
        key: `grant:${g.object}:${g.relation}`,
        target: isGroup ? g.object.replace(/^group:/, "") : g.object,
        role: isGroup ? "member" : g.relation,
        status: "approved",
        date: null,
        onRevoke: () =>
          confirmRemove(g.object, () =>
            revoke.mutate(
              { target: g.object, role: g.relation },
              {
                onSuccess: () => {
                  setNotice({
                    state: "success",
                    text: `Removed access to ${g.object}.`,
                  });
                },
              },
            ),
          ),
      };
    }),
    ...requests
      .filter((r) => r.requestType === "data-access" && r.status !== "approved")
      .map((r) => {
        const isGroup = r.target.startsWith("group:");
        return {
          key: `req:${r.id}`,
          target: isGroup ? r.target.replace(/^group:/, "") : r.target,
          role: isGroup ? "member" : r.requestedRole || undefined,
          status: r.status,
          date: r.requestedAt,
          onCancel:
            r.status === "pending" ? () => cancel.mutate(r.id) : undefined,
        };
      }),
  ];

  function openDrawer(mode: PermissionCategory) {
    setNotice(null);
    setDrawerMode(mode);
  }

  function confirmRemove(label: string, onConfirm: () => void) {
    setPendingRemoval({ label, onConfirm });
  }

  return (
    <Grid gap={32}>
      {notice && <Message state={notice.state}>{notice.text}</Message>}

      {/* The removal confirm modal closes as soon as the mutation fires, so a
          failed revoke or cancel lands here (the pattern ClientDetail uses for
          its mutation errors). */}
      {removeAudience.error && (
        <Message state="alert" title="Revoke failed">
          {removeAudience.error instanceof Error
            ? removeAudience.error.message
            : "Something went wrong."}
        </Message>
      )}
      {revoke.error && (
        <Message state="alert" title="Revoke failed">
          {revoke.error instanceof Error
            ? revoke.error.message
            : "Something went wrong."}
        </Message>
      )}
      {cancel.error && (
        <Message state="alert" title="Cancel failed">
          {cancel.error instanceof Error
            ? cancel.error.message
            : "Something went wrong."}
        </Message>
      )}

      {/* ── API access: who this client can call ── */}
      <section>
        <SectionHeader
          title="API access"
          description="APIs this client's token is allowed to call — on behalf of a signed-in user, or as itself."
          onAdd={canEdit ? () => openDrawer("api-access") : undefined}
        />

        {/* Quick grant: wire this client up to the sibling APIs it can't yet call */}
        {canEdit && missingApiAudiences.length > 0 && (
          <div style={cardStyle}>
            <Icon
              icon={faBolt}
              style={{ color: "var(--bfc-base-c)", flexShrink: 0 }}
            />
            <span className="bf-p" style={{ flex: 1, fontSize: "0.875em" }}>
              {missingApiAudiences.length === 1 ? (
                <>
                  Grant this client API access to{" "}
                  <strong>{missingApiAudiences[0]}</strong>?
                </>
              ) : (
                <>
                  Grant this client API access to{" "}
                  <strong>{missingApiAudiences.length}</strong> APIs in this
                  platform?
                </>
              )}
            </span>
            <Button
              small
              onClick={handleQuickGrant}
              disabled={audienceGrant.isPending}
            >
              {audienceGrant.isPending ? "Applying…" : "Apply"}
            </Button>
          </div>
        )}

        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>API</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Date</Table.HeaderCell>
              {canEdit && (
                <Table.HeaderCell
                  aria-label="Actions"
                  style={{ textAlign: "right" }}
                />
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {apiRows.length > 0 ? (
              apiRows.map((row) => (
                <Table.Row key={row.key}>
                  <Table.Cell>
                    <Mono large>{row.api}</Mono>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusText status={row.status} />
                  </Table.Cell>
                  <Table.Cell>
                    {row.date ? formatDate(row.date) : "—"}
                  </Table.Cell>
                  {canEdit && (
                    <Table.Cell style={{ textAlign: "right" }}>
                      <RowActions
                        onRevoke={row.onRevoke}
                        onCancel={row.onCancel}
                      />
                    </Table.Cell>
                  )}
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell
                  colSpan={canEdit ? 4 : 3}
                  className="bfc-base-2"
                  style={{ textAlign: "center" }}
                >
                  No API access yet.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </section>

      <Divider />

      {/* ── Data access: what this client can act on itself ── */}
      <section>
        <SectionHeader
          title="Data access"
          description="True permissions in the FGA store, same as user permissions."
          onAdd={canEdit ? () => openDrawer("data-access") : undefined}
        />

        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Object</Table.HeaderCell>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Date</Table.HeaderCell>
              {canEdit && (
                <Table.HeaderCell
                  aria-label="Actions"
                  style={{ textAlign: "right" }}
                />
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {dataRows.length > 0 ? (
              dataRows.map((row) => (
                <Table.Row key={row.key}>
                  <Table.Cell>
                    <Mono>{row.target}</Mono>
                  </Table.Cell>
                  <Table.Cell>
                    {row.role ? <RoleBadge role={row.role} /> : "—"}
                  </Table.Cell>
                  <Table.Cell>
                    <StatusText status={row.status} />
                  </Table.Cell>
                  <Table.Cell>
                    {row.date ? formatDate(row.date) : "—"}
                  </Table.Cell>
                  {canEdit && (
                    <Table.Cell style={{ textAlign: "right" }}>
                      <RowActions
                        onRevoke={row.onRevoke}
                        onCancel={row.onCancel}
                      />
                    </Table.Cell>
                  )}
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell
                  colSpan={canEdit ? 5 : 4}
                  className="bfc-base-2"
                  style={{ textAlign: "center" }}
                >
                  No data access yet.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </section>

      <Divider />

      <p className="bfc-base-2" style={{ margin: 0 }}>
        Learn more about the difference between{" "}
        <Link
          className="bf-link"
          to="/docs/authorization/managing-access#two-kinds-of-access"
        >
          API access and data access
        </Link>
        .
      </p>

      <AddPermissionDrawer
        clientId={clientId}
        isSpa={isSpa}
        mode={drawerMode}
        siblingApiClientIds={siblingApiClientIds}
        onClose={() => setDrawerMode(null)}
        onResult={(n) => {
          setNotice(n);
          setDrawerMode(null);
        }}
      />

      <Modal
        isOpen={pendingRemoval !== null}
        onRequestClose={() => setPendingRemoval(null)}
        header="Remove permission"
        width={480}
      >
        <Grid gap={16}>
          <p className="bf-p" style={{ margin: 0 }}>
            Remove access to <strong>{pendingRemoval?.label}</strong>? It might
            need approval again if you add it back.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="flat" onClick={() => setPendingRemoval(null)}>
              Cancel
            </Button>
            <Button
              variant="filled"
              state="alert"
              onClick={() => {
                pendingRemoval?.onConfirm();
                setPendingRemoval(null);
              }}
            >
              Remove
            </Button>
          </div>
        </Grid>
      </Modal>
    </Grid>
  );
}

const cardStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  padding: "10px 14px",
  border: "1px solid var(--bfc-base-c-dimmed)",
  borderRadius: 4,
};

function SectionHeader({
  title,
  description,
  onAdd,
}: {
  title: string;
  description: string;
  onAdd?: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 12,
      }}
    >
      <div>
        <h2 className="bf-h3" style={{ margin: 0 }}>
          {title}
        </h2>
        <p className="bfc-base-2" style={{ margin: "4px 0 0" }}>
          {description}
        </p>
      </div>
      {onAdd && (
        <Button variant="filled" onClick={onAdd} style={{ flexShrink: 0 }}>
          <Icon icon={faPlus} marginRight />
          Add
        </Button>
      )}
    </div>
  );
}

function Divider() {
  return (
    <hr
      style={{
        border: 0,
        borderTop: "1px solid var(--bfc-base-c-dimmed)",
        // Full-bleed: break out of the tab content's horizontal page padding
        // so the rule spans the whole panel, edge to edge.
        margin: "0 calc(var(--bf-page-padding) * -1)",
      }}
    />
  );
}

function Mono({
  children,
  large,
}: {
  children: React.ReactNode;
  large?: boolean;
}) {
  return (
    <span
      style={{
        fontFamily: "monospace",
        fontSize: large ? "1rem" : "0.875em",
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </span>
  );
}

function StatusText({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? {
    label: status,
    color: "var(--bfc-base-c-2)",
  };
  return (
    <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>
  );
}

function RowActions({
  onRevoke,
  onCancel,
}: {
  onRevoke?: () => void;
  onCancel?: () => void;
}) {
  if (onRevoke)
    return (
      <Button
        variant="basic"
        state="alert"
        noPadding
        aria-label="Revoke"
        onClick={onRevoke}
      >
        <Icon icon={faTrash} />
      </Button>
    );
  if (onCancel)
    return (
      <Button variant="flat" small onClick={onCancel}>
        Cancel
      </Button>
    );
  return null;
}

// Add-permission flow, in a slide-in drawer scoped to a single kind (the table
// it was opened from decides the kind — there is no type toggle). The form is
// only mounted while open, so its fields reset on each open. A successful grant
// (or a queued request) closes the drawer and surfaces the result as a notice
// in the section; only hard failures keep it open.
export function AddPermissionDrawer({
  clientId,
  isSpa,
  mode,
  siblingApiClientIds,
  onClose,
  onResult,
}: {
  clientId: string;
  isSpa: boolean;
  mode: PermissionCategory | null;
  siblingApiClientIds: string[];
  onClose: () => void;
  onResult: (notice: Notice) => void;
}) {
  return (
    <Drawer
      header={mode === "api-access" ? "Grant API access" : "Grant data access"}
      isOpen={mode !== null}
      onRequestClose={onClose}
    >
      {mode && (
        <AddPermissionForm
          clientId={clientId}
          isSpa={isSpa}
          mode={mode}
          siblingApiClientIds={siblingApiClientIds}
          onClose={onClose}
          onResult={onResult}
        />
      )}
    </Drawer>
  );
}

function AddPermissionForm({
  clientId,
  isSpa,
  mode,
  siblingApiClientIds,
  onClose,
  onResult,
}: {
  clientId: string;
  isSpa: boolean;
  mode: PermissionCategory;
  siblingApiClientIds: string[];
  onClose: () => void;
  onResult: (notice: Notice) => void;
}) {
  const audienceGrant = useAddAudienceGrant(clientId);
  const grant = useGrantAccess(clientId);
  // Only clients visible to this operator (useClients returns "my clients"),
  // and appType here can be wrong: ClientMapper.DetermineAppType falls back to
  // "worker" for any pre-stamp SPIFFE client, which is also true of unstamped
  // api clients like fga-api. So this is advice only — the server (which reads
  // the stamped registry:client-type) still decides; never block submit on it.
  const { data: knownClients = [] } = useClients();

  const [appGrantType, setAppGrantType] = useState<AppGrantType>("direct");
  const [audience, setAudience] = useState("");
  const [group, setGroup] = useState("");
  const [target, setTarget] = useState("");
  const [role, setRole] = useState<RuntimeRole>("reader");
  const [error, setError] = useState<string | null>(null);

  const pending = audienceGrant.isPending || grant.isPending;

  const audienceTarget = knownClients.find(
    (c) => c.clientId === audience.trim(),
  );
  const audienceNotGrantable =
    mode === "api-access" &&
    !!audienceTarget &&
    audienceTarget.appType !== "api";

  const approvalNote =
    mode === "api-access"
      ? "API access requires approval from the owner of the target API client."
      : appGrantType === "group"
        ? "Data access requires approval from the client owner and a group administrator."
        : "Data access requires approval from the client owner and an administrator of the requested data.";

  const canSubmit =
    !pending &&
    (mode === "api-access"
      ? !!audience.trim()
      : appGrantType === "group"
        ? !!group.trim()
        : !!target.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      if (mode === "api-access") {
        const val = audience.trim();
        const result = await audienceGrant.mutateAsync({ audience: val });
        onResult(
          result.requiresApproval
            ? queuedNotice(result.message)
            : { state: "success", text: `API access to '${val}' granted.` },
        );
      } else if (appGrantType === "group") {
        const val = group.trim();
        const result = await grant.mutateAsync({
          target: `group:${val}`,
          role: "member",
        });
        onResult(
          result.requiresApproval
            ? queuedNotice(result.message)
            : { state: "success", text: `Added to group '${val}'.` },
        );
      } else {
        const val = target.trim();
        const result = await grant.mutateAsync({ target: val, role });
        onResult(
          result.requiresApproval
            ? queuedNotice(result.message)
            : { state: "success", text: `Granted ${role} on ${val}.` },
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid gap={20} style={{ padding: "8px 20px 24px" }}>
        {mode === "data-access" && (
          <div>
            <span
              className="bf-label"
              style={{ display: "block", marginBottom: 8 }}
            >
              Access type
            </span>
            <Button.Group>
              <Button
                type="button"
                active={appGrantType === "group"}
                onClick={() => {
                  setAppGrantType("group");
                  setError(null);
                }}
              >
                Group
              </Button>
              <Button
                type="button"
                active={appGrantType === "direct"}
                onClick={() => {
                  setAppGrantType("direct");
                  setError(null);
                }}
              >
                Direct
              </Button>
            </Button.Group>
          </div>
        )}

        {mode === "api-access" && siblingApiClientIds.length > 0 && (
          <div>
            <span
              className="bf-label"
              style={{ display: "block", marginBottom: 8 }}
            >
              APIs in this platform
            </span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {siblingApiClientIds.map((id) => (
                <Button
                  key={id}
                  type="button"
                  variant="flat"
                  small
                  active={audience === id}
                  onClick={() => {
                    setAudience(id);
                    setError(null);
                  }}
                >
                  {id}
                </Button>
              ))}
            </div>
          </div>
        )}

        {mode === "api-access" && (
          <Input
            label="API client"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="e.g. fga-api"
            description="The clientId of the API this client is allowed to call — on behalf of a signed-in user, or as itself (client credentials)."
          />
        )}

        {audienceNotGrantable && (
          <Message state="warning">
            <strong>
              '{audienceTarget?.clientId}' looks like a{" "}
              {audienceTarget?.appType} client.
            </strong>{" "}
            {audienceTarget?.appType === "worker"
              ? "Workers have no inbound API surface, so they usually can't be granted as an API."
              : "SPAs have no inbound API surface, so they usually can't be granted as an API."}{" "}
            If it should be an 'api' client, register it as one. You can still
            try — the server has the final say.
          </Message>
        )}

        {mode === "api-access" &&
          isSpa &&
          audience.trim() &&
          !audienceNotGrantable &&
          !siblingApiClientIds.includes(audience.trim()) && (
            <Message state="warning">
              <strong>This grant makes the audience eager.</strong> The target
              API's audience will be included in every token this SPA receives.
              The recommended pattern is to have a confidential backend call the
              API on the user's behalf (<strong>token exchange</strong>). Only
              use direct SPA-to-API access if you have no backend component.
            </Message>
          )}

        {mode === "data-access" && appGrantType === "group" && (
          <Input
            label="Group"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            placeholder="e.g. equinor-developers"
            description="The group this client should become a member of."
          />
        )}

        {mode === "data-access" && appGrantType === "direct" && (
          <>
            <Input
              label="Object"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. platform:equinor/apm"
              description="The OpenFGA object this client should have access to."
            />
            <div>
              <span
                className="bf-label"
                style={{ display: "block", marginBottom: 8 }}
              >
                Role
              </span>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {RUNTIME_ROLES.map((r) => (
                  <RadioCard
                    key={r}
                    label={r}
                    name="direct-role"
                    checked={role === r}
                    onChange={() => setRole(r)}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        <Message state="neutral">{approvalNote}</Message>

        {/* Data access grants the object directly — but if the client reads that
            data by calling another API, that API also needs API access. */}
        {mode === "data-access" && (
          <div style={cardStyle}>
            <Icon
              icon={faCircleInfo}
              style={{ color: "var(--bfc-base-c)", flexShrink: 0 }}
            />
            <span className="bf-p" style={{ flex: 1, fontSize: "0.875em" }}>
              This grants a role on the object itself. If the client reaches
              that data by calling <em>another API</em>, that API also needs{" "}
              <strong>API access</strong> — add it from the API access table.
            </span>
          </div>
        )}

        {error && <Message state="alert">{error}</Message>}

        <div style={{ display: "flex", gap: 8 }}>
          <Button type="submit" state={canSubmit ? undefined : "inactive"}>
            {pending ? "Requesting…" : "Request access"}
          </Button>
          <Button
            type="button"
            variant="flat"
            onClick={pending ? undefined : onClose}
            state={pending ? "inactive" : undefined}
          >
            Cancel
          </Button>
        </div>
      </Grid>
    </form>
  );
}

function queuedNotice(message?: string): Notice {
  return {
    state: "warning",
    text: message ?? "Request queued for approval.",
  };
}
